import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { consumeRoleSwitchAnnouncement } from '@/lib/role-switch-announcement'

/**
 * Session 7 (item 2, UX-269): a single visually-hidden `aria-live="polite"`
 * region, mounted once in `RootLayout` (so it survives the navigation
 * `RoleSwitcher` performs — only `<Outlet />` swaps out), that speaks
 * "已切換至主辦方工作區"/"已切換至參賽者工作區" once the target workspace has
 * actually landed. `polite` (not `assertive`): a role switch is a
 * significant but non-urgent change — it should not interrupt whatever the
 * screen reader is already announcing about the new page's own content.
 *
 * Sets the live region's text imperatively via a ref inside the effect,
 * rather than through `useState`, so consuming the queued message (a
 * mutating `sessionStorage` read-and-clear, only ever meant to run once per
 * navigation) never drives a `setState`-in-effect re-render — the exact
 * anti-pattern `react-hooks/set-state-in-effect` flags, and the same
 * imperative-ref convention `student-team-page.tsx`'s focus-management
 * effect already established in this codebase for the same reason.
 */
export function RoleSwitchAnnouncer() {
  const location = useLocation()
  const regionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const message = consumeRoleSwitchAnnouncement()
    if (regionRef.current !== null) {
      // Cleared to '' (not left stale) on every navigation that did not
      // itself queue an announcement, so an old role-switch message never
      // lingers and gets re-announced on an unrelated later navigation.
      regionRef.current.textContent = message ?? ''
    }
  }, [location.pathname])

  return <div ref={regionRef} aria-live="polite" role="status" className="sr-only" />
}
